import { useState, useEffect, useRef } from 'react';
import { useTestStore, type TestId } from '@/store/useTestStore';
import { t } from '@/i18n/translations';
import { TestCard } from '@/components/TestCard';
import { MetricBox } from '@/components/MetricBox';
import { now } from '@/utils/format';
import { useFpsMeter } from '@/hooks/useFpsMeter';

interface Props { autoRun: boolean; autoNonce: number; }

interface BatteryManager {
  charging: boolean;
  level: number;
  addEventListener(type: string, fn: () => void): void;
  removeEventListener(type: string, fn: () => void): void;
}

export function BatteryTest({ autoRun, autoNonce }: Props) {
  const { language, reportResult, onAutoDone } = useTestStore();
  const [running, setRunning] = useState(false);
  const [batteryApi, setBatteryApi] = useState<boolean | null>(null);
  const [batteryLevel, setBatteryLevel] = useState<number | null>(null);
  const [charging, setCharging] = useState<boolean | null>(null);
  const [throttleDetected, setThrottleDetected] = useState(false);
  const fps = useFpsMeter(running);
  const fpsHistory = useRef<number[]>([]);
  const autoDoneRef = useRef(false);
  const rafRef = useRef(0);

  function reset() {
    setRunning(false);
    autoDoneRef.current = false;
    fpsHistory.current = [];
    setBatteryApi(null);
    setBatteryLevel(null);
    setCharging(null);
    setThrottleDetected(false);
    cancelAnimationFrame(rafRef.current);
  }

  async function start() {
    reset();
    setRunning(true);

    // Check battery API
    try {
      const nav = navigator as Navigator & { getBattery?: () => Promise<BatteryManager> };
      if (nav.getBattery) {
        const batt = await nav.getBattery();
        setBatteryApi(true);
        setBatteryLevel(batt.level);
        setCharging(batt.charging);
      } else {
        setBatteryApi(false);
      }
    } catch {
      setBatteryApi(false);
    }

    // Sustained CPU load to detect throttling
    fpsHistory.current = [];
    const startTime = now();
    function cpuLoop() {
      if (now() - startTime > 8000) return; // 8s of load
      // Intentional CPU burn
      let x = 0;
      for (let i = 0; i < 500000; i++) x = (x + i) % 97;
      rafRef.current = requestAnimationFrame(cpuLoop);
    }
    rafRef.current = requestAnimationFrame(cpuLoop);
  }

  useEffect(() => {
    if (running && fps > 0) {
      fpsHistory.current.push(fps);
      // Check for throttling: if FPS drops significantly over time
      if (fpsHistory.current.length > 10) {
        const first5 = fpsHistory.current.slice(0, 5);
        const last5 = fpsHistory.current.slice(-5);
        const avgFirst = first5.reduce((a, b) => a + b, 0) / first5.length;
        const avgLast = last5.reduce((a, b) => a + b, 0) / last5.length;
        if (avgLast < avgFirst * 0.7) setThrottleDetected(true);
      }
    }
  }, [fps, running]);

  function stop() {
    cancelAnimationFrame(rafRef.current);
    setRunning(false);

    let sc = 50;
    if (batteryApi) sc += 15;
    if (!throttleDetected) sc += 25;
    else sc -= 10;
    if (batteryLevel != null && batteryLevel > 0.2) sc += 10;
    sc = Math.max(10, Math.min(100, sc));

    const testId: TestId = 'mobile-battery';
    reportResult(testId, {
      score: sc,
      primary: `Battery: ${batteryLevel != null ? (batteryLevel * 100).toFixed(0) + '%' : 'n/a'} · Throttle: ${throttleDetected ? 'YES' : 'NO'}`,
      details: { batteryApi, batteryLevel, charging, throttleDetected },
    });
    if (autoRun && !autoDoneRef.current) { autoDoneRef.current = true; onAutoDone(testId); }
  }

  useEffect(() => {
    if (!autoRun) return;
    start();
    const timer = setTimeout(() => stop(), 8000);
    return () => { clearTimeout(timer); cancelAnimationFrame(rafRef.current); };
  }, [autoRun, autoNonce]);

  return (
    <TestCard title={t('mobile.battery.title', language)} description={t('mobile.battery.desc', language)}>
      <div className="flex flex-wrap gap-2.5 items-center">
        <button className="btn btn-primary" onClick={start} disabled={running || autoRun}>{t('btn.start', language)}</button>
        <button className="btn btn-danger" onClick={stop} disabled={!running || autoRun}>{t('btn.stop', language)}</button>
        <button className="btn" onClick={reset} disabled={autoRun}>{t('btn.reset', language)}</button>
      </div>
      <div className="grid grid-cols-4 gap-2.5 mt-3 max-lg:grid-cols-2 max-sm:grid-cols-1">
        <MetricBox label={t('battery.api', language)} value={batteryApi === null ? '—' : batteryApi ? t('supported.yes', language) : t('supported.no', language)} />
        <MetricBox label={t('battery.level', language)} value={batteryLevel != null ? (batteryLevel * 100).toFixed(0) + '%' : '—'} />
        <MetricBox label={t('battery.charging', language)} value={charging === null ? '—' : charging ? t('supported.yes', language) : t('supported.no', language)} />
        <MetricBox label={t('battery.throttle', language)} value={running ? (throttleDetected ? 'DETECTED' : 'monitoring…') : throttleDetected ? 'DETECTED' : '—'} hint="FPS drop under sustained load" />
      </div>
      {running && (
        <div className="mt-3 text-xs text-muted">
          FPS under load: <span className="font-mono text-white">{fps.toFixed(1)}</span>
          {' · Samples: '}{fpsHistory.current.length}
        </div>
      )}
    </TestCard>
  );
}
